/**
 * FETC SEO Article Redesign — JavaScript
 * Deploy to: assets/fetc-redesign-seo.js (new file, upgrade-safe)
 * Loaded by: templates/article.fetc-encyclopedia.json via <script defer>
 * Scope: body.template-suffix-fetc-encyclopedia only
 *
 * Features:
 *   1. TOC IntersectionObserver — highlights active TOC link as user scrolls
 *   2. TOC client-side build — fallback if server-side parse found no headings
 *   3. FAQ accordion — ensure only one item open at a time (optional UX choice)
 *   4. Smooth scroll — native scroll-behavior: smooth via CSS is preferred;
 *      this JS handles focus management for keyboard users
 *   5. Feedback widget — "Was this guide helpful?" Yes/No handler
 *
 * No dependencies. Vanilla JS only.
 * Target: Chrome 90+, Safari 14+, Firefox 88+ (all support IntersectionObserver v2).
 */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────
     Guard: only run on fetc-encyclopedia template
     ────────────────────────────────────────────────────────── */
  if (!document.body.classList.contains('template-suffix-fetc-encyclopedia')) return;

  /* ──────────────────────────────────────────────────────────
     1. TOC IntersectionObserver — active link highlight
     ────────────────────────────────────────────────────────── */
  var ACTIVE_CLASS = 'is-active';
  /* Existing main-article.liquid generates: <details id="toc"> > <ul class="toc-container"> > <li> > <a href="#toc-N">.
     Each heading also receives id="toc-N". Prefer the new fetc-toc-list id if a future
     template provides one; fall back to the legacy .toc-container structure. */
  var tocList = document.getElementById('fetc-toc-list')
             || document.querySelector('#toc .toc-container');
  var tocLinks = tocList ? tocList.querySelectorAll('a[href^="#"]') : [];

  /**
   * Build a map of { sectionId → tocLinkElement }. Reads `data-toc-target`
   * when present (fetc-toc-list) or falls back to the `href="#section-id"` value.
   */
  function buildTocLinkMap(links) {
    var map = {};
    links.forEach(function (link) {
      var target = link.getAttribute('data-toc-target');
      if (!target) {
        var href = link.getAttribute('href') || '';
        if (href.charAt(0) === '#' && href.length > 1) {
          target = href.slice(1);
        }
      }
      if (target) map[target] = link;
    });
    return map;
  }

  /**
   * Remove is-active from all TOC links, then add it to the active one.
   */
  function setActiveTocLink(linkMap, activeSectionId) {
    Object.keys(linkMap).forEach(function (id) {
      linkMap[id].classList.remove(ACTIVE_CLASS);
    });
    if (activeSectionId && linkMap[activeSectionId]) {
      linkMap[activeSectionId].classList.add(ACTIVE_CLASS);
    }
  }

  /**
   * Initialise IntersectionObserver on all H2/H3 headings that have an id
   * matching a TOC anchor. The topmost visible heading is considered active.
   */
  function initTocObserver() {
    if (!tocList || tocLinks.length === 0) return;

    var linkMap = buildTocLinkMap(tocLinks);
    var sectionIds = Object.keys(linkMap);
    if (sectionIds.length === 0) return;

    /* Collect heading elements from page */
    var headings = [];
    sectionIds.forEach(function (id) {
      var el = document.getElementById(id);
      if (el) headings.push(el);
    });
    if (headings.length === 0) return;

    /* Track which headings are currently intersecting */
    var visibleSet = new Set();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var id = entry.target.id;
          if (entry.isIntersecting) {
            visibleSet.add(id);
          } else {
            visibleSet.delete(id);
          }
        });

        /* Determine topmost visible heading (first in DOM order) */
        var activeSectionId = null;
        headings.forEach(function (heading) {
          if (activeSectionId === null && visibleSet.has(heading.id)) {
            activeSectionId = heading.id;
          }
        });

        /* If nothing visible, pick the last heading that has scrolled past viewport top */
        if (activeSectionId === null) {
          var scrollY = window.scrollY || window.pageYOffset;
          var lastPast = null;
          headings.forEach(function (heading) {
            var rect = heading.getBoundingClientRect();
            if (rect.top + scrollY <= scrollY + 80) {
              lastPast = heading.id;
            }
          });
          activeSectionId = lastPast;
        }

        setActiveTocLink(linkMap, activeSectionId);
      },
      {
        /* Trigger when heading enters/exits the top 20% of the viewport */
        rootMargin: '-80px 0px -75% 0px',
        threshold: 0
      }
    );

    headings.forEach(function (heading) {
      observer.observe(heading);
    });
  }

  /* ──────────────────────────────────────────────────────────
     2. TOC client-side build — fallback
     Existing main-article.liquid (fareastteaclub blog) already builds the TOC
     server-side via inline <script>. This fallback only runs when a future
     template supplies a #fetc-toc-list with the --js-pending class but no items.
     ────────────────────────────────────────────────────────── */
  function buildTocFromDom() {
    if (!tocList) return;
    if (!tocList.classList || !tocList.classList.contains('fetc-toc-panel__list--js-pending')) return;

    /* Read all H2 headings with id from .post-body */
    var postBody = document.querySelector('.post-body');
    if (!postBody) return;

    var headings = postBody.querySelectorAll('h2[id]');
    if (headings.length === 0) return;

    headings.forEach(function (heading) {
      var id = heading.id;
      var text = heading.textContent.trim();

      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'fetc-toc-panel__link';
      a.href = '#' + id;
      a.setAttribute('data-toc-target', id);
      a.textContent = text;
      li.appendChild(a);
      tocList.appendChild(li);
    });

    /* Remove pending class so observer can pick up the new links */
    tocList.classList.remove('fetc-toc-panel__list--js-pending');

    /* Re-query links now that they exist */
    tocLinks = tocList.querySelectorAll('a[href^="#"]');
  }

  /* ──────────────────────────────────────────────────────────
     3. FAQ accordion — single-open mode
     By default, <details> elements operate independently.
     This closes others when one opens (optional; remove if multi-open is preferred).
     ────────────────────────────────────────────────────────── */
  function initFaqAccordion() {
    var faqSection = document.querySelector('.fetc-faq');
    if (!faqSection) return;

    var items = faqSection.querySelectorAll('details.fetc-faq__item');
    if (items.length === 0) return;

    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (item.open) {
          items.forEach(function (other) {
            if (other !== item && other.open) {
              other.removeAttribute('open');
            }
          });
        }
      });
    });
  }

  /* ──────────────────────────────────────────────────────────
     4. Smooth scroll with focus management
     Intercepts clicks on .fetc-toc-panel__link and manages focus
     for keyboard/screen reader users after scrolling.
     Native scroll-behavior: smooth handles visual smoothness via CSS.
     ────────────────────────────────────────────────────────── */
  function initSmoothScrollFocus() {
    if (!tocList) return;

    tocList.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;

      var targetId = link.getAttribute('href').slice(1);
      var targetEl = document.getElementById(targetId);
      if (!targetEl) return;

      /* Ensure the target heading is focusable for screen readers */
      if (!targetEl.hasAttribute('tabindex')) {
        targetEl.setAttribute('tabindex', '-1');
      }

      /* Allow native smooth scroll to happen, then set focus */
      setTimeout(function () {
        targetEl.focus({ preventScroll: true });
      }, 350);
    });
  }

  /* Section 5 (initFeedbackWidget) removed — feedback widget retired from design 2026-05.
     CSS for .fetc-feedback was already absent; DOM is never produced by any snippet. */

  /* ──────────────────────────────────────────────────────────
     5. Epigraph extraction
     Scans the first non-empty <p> inside the RTE container and
     optionally converts it into <blockquote class="fetc-epigraph">.

     Logic mirrors build_preview.py split_epigraph() exactly:

     Case A — EN/FR dash-split: "quote" — attribution
       Pattern: opening curly/straight double-quote  →  em/en/hyphen-minus dash  →  rest
       → <blockquote class="fetc-epigraph">quote</blockquote>
          <p>Attribution (sentence-cased)</p>
       Trigger: text starts with " or " AND regex matches "quote" — rest

     Case B — standalone short or fully-quoted paragraph
       - len(text) <= 130  OR
       - first char in " " « 「 『  AND  last char in " " » 」 』
       → <blockquote class="fetc-epigraph">inner HTML</blockquote>
         (CJK quoting brackets kept intact inside the blockquote)

     Case C — anything else → no-op (first <p> becomes the drop-cap lead)

     Idempotency guard: if the element immediately before the first <p>
     is already a .fetc-epigraph blockquote, the function returns early.
     ────────────────────────────────────────────────────────── */
  function initEpigraph() {
    /* SEO RTE selector: .rte.post-body (or just .post-body) */
    var rte = document.querySelector('.rte.post-body') ||
              document.querySelector('.post-body');
    if (!rte) return;

    /* Find first non-empty <p> */
    var paragraphs = rte.querySelectorAll('p');
    var firstP = null;
    for (var i = 0; i < paragraphs.length; i++) {
      if (paragraphs[i].textContent.trim() !== '') {
        firstP = paragraphs[i];
        break;
      }
    }
    if (!firstP) return;

    /* Idempotency: already processed if previous sibling is .fetc-epigraph */
    var prev = firstP.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('fetc-epigraph')) return;

    var inner = firstP.innerHTML.trim();
    /* Strip HTML tags for plain-text checks */
    var text = inner.replace(/<[^>]+>/g, '').trim();

    /* Case A — EN/FR: "quote" — attribution
       Python regex: r'^\s*([""][^"""]+[""])\s*[—–-]\s*(.+)$'
       + guard: text[:1] in '""'
       We replicate with a JS RegExp on inner (which may contain spans/em).
       For the text-start guard we use the stripped text. */
    var OPEN_DOUBLE = '“"'; /* " " */
    if (OPEN_DOUBLE.indexOf(text.charAt(0)) !== -1) {
      /* Match on inner HTML: opening quote char, content, closing quote char,
         optional whitespace, em/en/hyphen dash, rest */
      var dashRe = /^\s*([“"][^“”"]+[”"])\s*[—–\-]\s*(.+)$/s;
      var mq = dashRe.exec(inner);
      if (mq) {
        var epiContent = mq[1].trim();
        var rest = mq[2].trim();
        /* Sentence-case the attribution (capitalise first char) */
        if (rest.length > 0) {
          rest = rest.charAt(0).toUpperCase() + rest.slice(1);
        }
        var bq = document.createElement('blockquote');
        bq.className = 'fetc-epigraph';
        bq.innerHTML = epiContent;
        var newP = document.createElement('p');
        newP.innerHTML = rest;
        firstP.parentNode.insertBefore(bq, firstP);
        firstP.parentNode.insertBefore(newP, firstP);
        firstP.parentNode.removeChild(firstP);
        return;
      }
    }

    /* Case B — standalone short or fully-quoted paragraph
       Python: text[:1] in '""«「『' and text[-1:] in '""»」』'
       threshold: len(text) <= 130 */
    var OPEN_QUOTES  = '“"«「『'; /* " " « 「 『 */
    var CLOSE_QUOTES = '”"»」』'; /* " " » 」 』 */
    var isQuoted = (OPEN_QUOTES.indexOf(text.charAt(0)) !== -1) &&
                   (CLOSE_QUOTES.indexOf(text.charAt(text.length - 1)) !== -1);
    if (text.length <= 130 || isQuoted) {
      var bqB = document.createElement('blockquote');
      bqB.className = 'fetc-epigraph';
      bqB.innerHTML = inner;
      firstP.parentNode.insertBefore(bqB, firstP);
      firstP.parentNode.removeChild(firstP);
      return;
    }
    /* Case C — no-op */
  }

  /* ──────────────────────────────────────────────────────────
     Init — run after DOM is ready
     ────────────────────────────────────────────────────────── */
  /* ──────────────────────────────────────────────────────────
     TOC sticky offset — sticky ヘッダー(#pageheader)の実高さに追従。
     固定 96px だとヘッダー(2段)に目次上部が隠れるため動的に算出。
     ────────────────────────────────────────────────────────── */
  function initTocStickyOffset() {
    var toc = document.getElementById('toc');
    if (!toc) { return; }
    var header = document.getElementById('pageheader');
    /* sticky ヘッダーが画面上で占める下端(px)を返す。
       - 実装が position:fixed/sticky で固定されている時はその実 rect.bottom を採用
         (テーマJSによる固定クローン/高さ変化に左右されない)
       - まだ固定されていない(top にいる)時は自然高さ offsetHeight
       - relative でスクロールアウトした場合は 0 */
    function stuckBottom() {
      if (!header) { return 0; }
      var cs = window.getComputedStyle(header);
      var pos = cs.position;
      if (pos === 'fixed' || pos === 'sticky') {
        var b = header.getBoundingClientRect().bottom;
        return b > 0 ? b : 0;
      }
      /* relative: 画面内に残っている分だけ採用(スクロールアウトで自然に 0 へ) */
      var rb = header.getBoundingClientRect().bottom;
      if (rb > 0 && rb <= window.innerHeight) { return rb; }
      if (header.classList.contains('pageheader--sticky')) { return header.offsetHeight || 0; }
      return 0;
    }
    var ticking = false;
    function setTop() {
      var h = stuckBottom();
      /* 変数は body.template-suffix-fetc-encyclopedia スコープで定義されているため
         body にインラインで設定して上書き(:root では #toc に効かない) */
      var el = document.body;
      el.style.setProperty('--fetc-toc-top', Math.round(h + 20) + 'px');
      el.style.setProperty('--fetc-toc-bottom-pad', Math.round(h + 52) + 'px');
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(setTop); }
    }
    setTop();
    window.addEventListener('resize', setTop);
    window.addEventListener('load', setTop);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ──────────────────────────────────────────────────────────
     TOC open 状態 — デスクトップ(≥1100px)は常時展開、モバイルは既定クローズ。
     liquid 側は open 無しで出力 → ここで viewport に応じて制御。
     ────────────────────────────────────────────────────────── */
  function initTocResponsive() {
    var toc = document.getElementById('toc');
    if (!toc) { return; }
    var mq = window.matchMedia('(min-width: 1100px)');
    function apply() {
      if (mq.matches) { toc.setAttribute('open', ''); }
      else { toc.removeAttribute('open'); }
    }
    apply();
    if (mq.addEventListener) { mq.addEventListener('change', apply); }
    else if (mq.addListener) { mq.addListener(apply); }
  }

  function init() {
    initEpigraph();        /* Before TOC build — epigraph shifts DOM order */
    buildTocFromDom();    /* Must run before initTocObserver so links exist */
    initTocObserver();
    initTocStickyOffset();
    initTocResponsive();
    initFaqAccordion();
    initSmoothScrollFocus();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
